'use client';

import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { CLASS_AVAILABILITY_WINDOW_DAYS } from '@kit/bookings/constants';

import type {
  BookingAction,
  BookingState,
  BookingType,
  DurationOption,
  TimeSlot,
} from './use-booking-reducer';
import { isClassType } from './use-booking-reducer';
import { shouldShowServiceList } from '../is-service-list-sport';
import { isDiscountVariantOf } from '../trainer-price-collision';

type UnifiedSlotsActionResult = {
    success: boolean;
    data?: Array<{
      start_time: string;
      end_time: string;
      is_available: boolean;
      available_court_ids: string[];
      available_courts: Array<{
        id: string;
        name: string;
        name_ar?: string | null;
        type: string;
        type_ar?: string | null;
        template_id?: string;
        original_price?: number | null;
        final_price?: number | null;
      }>;
      is_peak?: boolean;
      resource_ids?: string[];
      resource_names?: string[];
      available_resources?: Array<{
        id: string;
        available_court_ids?: string[];
        name: string;
        bio: string | null;
        picture_url: string | null;
        normal_price: number;
        peak_price: number;
        member_price?: number | null;
      }>;
      // Phase B: server-resolved pricing & perk metadata. Optional because
      // legacy callers / older RPC shapes may omit them.
      original_price?: number | null;
      final_price?: number | null;
      member_discount?: {
        addon_id: string;
        type: 'percent' | 'fixed';
        value: number;
      } | null;
    }>;
    error?: string;
};

interface BookingEffectsParams {
  state: BookingState;
  dispatch: React.Dispatch<BookingAction>;
  tenantSlug: string;
  userId?: string;
  /** True until the auth session resolves; userId is undefined meanwhile even
   *  for signed-in customers, so user-scoped fetches must wait for it. */
  isUserLoading: boolean;
  /** Active payment provider. The native Apple Pay button is a Moyasar-only
   *  integration; Stripe (and others) surface Apple Pay inside their own
   *  checkout sheet, so we only treat Apple Pay as available for Moyasar. */
  provider?: string | null;
  /** `business_settings.business_type`; must match what the form passes to
   *  shouldShowServiceList so the slot fetch and the UI agree. */
  businessType?: string | null;
  /**
   * Batched slot fetch — ONE server-action POST for every template in the
   * current (sport, duration) fan-out, instead of one POST per template.
   * Per-template results come back tagged so the merge below is unchanged.
   */
  getAllBookingSlotsBatch: (
    templateIds: string[],
    date: string,
    bookingType: 'court' | 'trainer' | 'ball_machine',
    userId?: string,
    locationId?: string,
    dynamicSlotMode?: boolean,
    language?: string,
  ) => Promise<
    | {
        success: true;
        results: Array<{ templateId: string; result: UnifiedSlotsActionResult }>;
      }
    | { success: false; error: string }
  >;
  getAvailableClassesForDate: (
    tenantSlug: string,
    date: string,
    sportId?: string,
    category?: string | null,
    endDate?: string,
    userId?: string,
    locationId?: string | null,
  ) => Promise<{
    success: boolean;
    data?: BookingState['availableClasses'];
    error?: string;
  }>;
  checkUserPackageCreditsAction: (params: {
    packageType:
      | 'court'
      | 'trainer'
      | 'group_classes'
      | 'semi_private'
      | 'kids_academy';
    templateId?: string;
    targetUserId?: string;
    courtId?: string;
    isPeak?: boolean;
    trainerId?: string;
    bookingDate?: string;
    numberOfParticipants?: number;
  }) => Promise<{
    success: boolean;
    data?: {
      availableCredits: number;
      hasMatchingTemplate: boolean;
      hasDateExcludedCredits?: boolean;
    };
  }>;
}

interface BookingEffectsReturn {
  durationOptions: DurationOption[];
  getDurationFromLabel: (label: string) => number;
  /**
   * Builds the canonical duration label for N minutes (e.g. "60 Minutes").
   * The service list uses this so the label it dispatches in SELECT_SERVICE
   * round-trips through getDurationFromLabel in the slot-fetch effect.
   */
  formatDuration: (minutes: number) => string;
}

function formatDateLocal(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function mapBookingTypeToPackageType(
  bookingType: BookingType,
): 'court' | 'trainer' | 'group_classes' {
  if (bookingType === 'trainer') return 'trainer';
  if (bookingType === 'ball_machine') return 'court';
  // All class-type categories (group_classes, kids_academy, Floor, etc.)
  // use 'group_classes' for package credit matching
  if (isClassType(bookingType)) return 'group_classes';
  return 'court';
}

export function useBookingEffects(
  params: BookingEffectsParams,
): BookingEffectsReturn {
  const {
    state,
    dispatch,
    tenantSlug,
    userId,
    isUserLoading,
    provider,
    businessType,
    getAllBookingSlotsBatch,
    getAvailableClassesForDate,
    checkUserPackageCreditsAction,
  } = params;

  const { t, i18n } = useTranslation('bookings');

  const formatDuration = useCallback(
    (minutes: number): string => {
      return t('minDuration', { count: minutes });
    },
    [t],
  );

  // Compute duration options from state
  const durationOptions = useMemo<DurationOption[]>(() => {
    if (!state.sport || isClassType(state.bookingType)) {
      return [];
    }

    const sportDurationsByType =
      state.allDurationsByBookingType[state.sport];
    const sportDurations =
      sportDurationsByType?.[state.bookingType] ??
      state.allDurationsBySport[state.sport] ??
      [];

    const options: DurationOption[] = sportDurations.map((duration) => ({
      duration,
      isAvailable: true,
      label: formatDuration(duration),
    }));

    options.sort((a, b) => a.duration - b.duration);
    return options;
  }, [
    state.sport,
    state.bookingType,
    state.allDurationsByBookingType,
    state.allDurationsBySport,
    formatDuration,
  ]);

  const getDurationFromLabel = useCallback(
    (label: string): number => {
      const option = durationOptions.find((opt) => opt.label === label);
      return option ? option.duration : 0;
    },
    [durationOptions],
  );

  // Identity of the last slot fetch we actually ran. Effect 1 lists
  // state.selectedTemplateId in its deps (needed so picking a named service
  // re-fetches), but SET_TIME_SLOTS *also* writes selectedTemplateId back
  // (''→ matchingTemplates[0].id). Without this guard that write-back
  // re-triggers the effect, causing a redundant second fetch and a visible
  // skeleton flash on the time grid. We skip the re-run when the resolved
  // fetch inputs are unchanged.
  //
  // The ref is reset whenever the date changes (see below). Slot availability
  // is time-sensitive, so navigating back to a previously-visited date must
  // re-query the server rather than reuse a key that still matches a stale
  // fetch — without the reset the guard would wrongly skip that re-fetch and
  // show slots cached from the earlier visit.
  const lastSlotFetchKey = useRef<string>('');

  // Clear the de-dup key on every date change so date revisits always re-fetch
  // fresh availability. Cheap: the slot effect below still owns the actual
  // fetch + skeleton; this only invalidates the "already fetched this" memo.
  const lastDateKey = useRef<string>('');
  const currentDateKey = state.date ? formatDateLocal(state.date) : '';
  if (lastDateKey.current !== currentDateKey) {
    lastDateKey.current = currentDateKey;
    lastSlotFetchKey.current = '';
  }

  // -------------------------------------------------------------------
  // Effect 1: Fetch time slots (non-class bookings)
  // -------------------------------------------------------------------
  useEffect(() => {
    if (
      !state.date ||
      !state.selectedDuration ||
      !state.sport ||
      isClassType(state.bookingType)
    ) {
      return;
    }

    const duration = getDurationFromLabel(state.selectedDuration);

    // Validate the duration is valid for the current sport
    const sportDurationsByType =
      state.allDurationsByBookingType[state.sport];
    const validDurations =
      sportDurationsByType?.[state.bookingType] ??
      state.allDurationsBySport[state.sport] ??
      [];

    if (!validDurations.includes(duration)) {
      dispatch({
        type: 'SET_TIME_SLOTS',
        payload: { timeSlots: [], templateId: '' },
      });
      return;
    }

    // Find ALL templates matching sport + duration + bookingType
    // Multiple templates may exist for the same duration (e.g. premium vs regular courts)
    const sportTemplates = state.allTemplatesBySport[state.sport] ?? [];

    // Templates for this sport + booking type, across ALL durations. Mirrors the
    // form's `serviceTemplates` so the service-list decision below matches what
    // the UI actually rendered.
    const bookingTypeTemplates = sportTemplates.filter((t) => {
      if (state.bookingType === 'court') return t.has_court_booking;
      if (state.bookingType === 'trainer') return t.has_trainer_booking;
      if (state.bookingType === 'ball_machine')
        return t.has_ball_machine_booking;
      return false;
    });

    // The duration-resolved set: every template for the picked duration. This is
    // what the merge below fans out over (e.g. ActivePadel's Indoor-60 +
    // Outdoor-60 on disjoint courts).
    const durationTemplates = bookingTypeTemplates.filter(
      (t) => t.duration === duration,
    );

    // When the customer has genuinely pinned a single template — via the
    // service-list flow (SELECT_SERVICE) — fetch slots for that one template
    // only, bypassing the same-duration merge so the chosen service's
    // pricing/identity is preserved.
    //
    // We must NOT honor the pin in the duration-chip flow (for ANY booking type).
    // There, selectedTemplateId is set only by SET_TIME_SLOTS' write-back
    // (= matchingTemplates[0].id), NOT by a real user choice. Honoring it would
    // re-fire this effect and collapse a multi-template merge down to that one
    // template, silently dropping every other same-duration template's resources:
    //   • court   — the Indoor-vs-Outdoor "only outdoor shows" bug.
    //   • trainer — the disjoint-coach "only the 400 coaches show" bug: two
    //     same-duration coaching templates (e.g. Private @400 + Private–Asaad
    //     & Hadi @330) on disjoint coaches now render duration chips (see
    //     shouldShowServiceList), so honoring the write-back pin would drop one
    //     template's coaches from the trainer picker entirely.
    // The service list is the single signal that a REAL template choice was made
    // (it's shown only when same-duration templates SHARE a resource, where the
    // user must disambiguate up front via SELECT_SERVICE). So honor the pin iff
    // the service list is shown for this booking type — the same condition the
    // form uses to render it.
    const honorPin = shouldShowServiceList(
      state.bookingType,
      bookingTypeTemplates,
      businessType,
    );

    const pinnedTemplate =
      honorPin && state.selectedTemplateId
        ? sportTemplates.find((t) => t.id === state.selectedTemplateId)
        : undefined;

    const matchingTemplates = pinnedTemplate
      ? [pinnedTemplate]
      : durationTemplates;

    if (matchingTemplates.length === 0) {
      dispatch({
        type: 'SET_TIME_SLOTS',
        payload: { timeSlots: [], templateId: '' },
      });
      return;
    }

    // Wait for the session so a signed-in customer's slots are not first
    // fetched in guest mode and then refetched with their user id.
    if (isUserLoading) {
      dispatch({ type: 'SET_TIME_SLOTS_LOADING', payload: true });
      return;
    }

    // Skip the fetch when nothing that affects the slot query has actually
    // changed. SET_TIME_SLOTS writes selectedTemplateId back onto the state,
    // which re-fires this effect; without this guard that produces a second
    // identical fetch and a skeleton flash. The key intentionally uses the
    // RESOLVED template ids (not state.selectedTemplateId directly) so the
    // ''→ matchingTemplates[0].id write-back collapses to the same key.
    //
    // Language gates the server-side Arabic court name/type overlay and is
    // part of the fetch key. Client i18n briefly reports the 'en' fallback on
    // first paint before settling to 'ar', which used to fire the ENTIRE
    // fan-out twice for every Arabic session (once 'en', once 'ar'). The SSR
    // layout stamps the settled locale on <html lang> before hydration, so
    // prefer that — it is already correct on the first run. i18n.language
    // stays as the fallback and as an effect dependency so the i18n settle
    // (and any future runtime language switch) still re-runs the effect; with
    // the DOM value preferred, that re-run now collapses to the SAME key and
    // the duplicate fetch is skipped. NOTE: /book has no runtime language
    // switcher today — if one lands without syncing document.documentElement
    // .lang, sync it there (or the overlay won't follow the switch).
    const effectiveLanguage =
      (typeof document !== 'undefined' && document.documentElement.lang) ||
      i18n.language;
    const formattedDateForKey = formatDateLocal(state.date);
    const fetchKey = [
      formattedDateForKey,
      duration,
      state.sport,
      state.bookingType,
      state.selectedLocationId ?? '',
      userId ?? '',
      matchingTemplates.map((tmpl) => tmpl.id).join(','),
      effectiveLanguage,
      state.timeSlotsRefreshCount,
    ].join('|');

    if (lastSlotFetchKey.current === fetchKey) {
      return;
    }
    lastSlotFetchKey.current = fetchKey;

    // AbortController is used to prevent stale results from updating state
    // after the effect has been torn down (e.g. user changed the date before
    // the previous fetch completed). Server actions cannot be cancelled
    // mid-flight, so the signal is not passed to the action itself — it only
    // guards the dispatch call.
    const abortController = new AbortController();

    const fetchTimeSlots = async () => {
      dispatch({ type: 'SET_TIME_SLOTS_LOADING', payload: true });

      const formattedDate = formatDateLocal(state.date);
      const dynamicSlotMode = state.sport
        ? state.dynamicSlotSports.has(state.sport)
        : false;

      try {
        // ONE batched server-action call for all matching templates. The
        // server fans out over the templates in parallel with allSettled, so
        // a single failing template (e.g. premium courts temporarily
        // unavailable) still cannot discard results from the others — same
        // isolation the previous client-side Promise.allSettled provided,
        // without N action POSTs per date click.
        const batch = await getAllBookingSlotsBatch(
          matchingTemplates.map((tmpl) => tmpl.id),
          formattedDate,
          state.bookingType as 'court' | 'trainer' | 'ball_machine',
          userId,
          state.selectedLocationId ?? undefined,
          dynamicSlotMode,
          effectiveLanguage,
        );

        if (abortController.signal.aborted) return;

        if (!batch.success) {
          console.error('Error fetching slots batch:', batch.error);
        }

        const results = batch.success
          ? batch.results.map(({ templateId, result }) => ({
              result,
              templateId,
            }))
          : [];

        // Merge time slots across templates, keyed by "HH:MM_HH:MM".
        // A slot is available if ANY template reports it available.
        // Courts are tagged with their source template_id so the reducer can
        // look up the correct pricing when a court is selected.
        const slotMap = new Map<string, TimeSlot>();

        for (const { result, templateId: tmplId } of results) {
          if (!result.success || !result.data) continue;

          for (const slot of result.data) {
            const timeKey = `${slot.start_time}_${slot.end_time}`;

            if (!slotMap.has(timeKey)) {
              slotMap.set(timeKey, {
                start_time: slot.start_time,
                end_time: slot.end_time,
                is_available: false,
                available_court_ids: [],
                available_courts: [],
                // Seed is_peak from the first template for this window; will be
                // upgraded to true below if any subsequent template marks it peak.
                is_peak: slot.is_peak,
                resource_ids: [],
                resource_names: [],
                available_resources: [],
                // Phase B: carry server-resolved pricing + perk metadata.
                // For multi-template merges, the per-court `template_id` lets
                // the reducer resolve the right price from
                // `available_resources` / template prices when the user picks
                // a court; this slot-level value is the single-template
                // fast path that the reducer reads first.
                original_price: slot.original_price ?? null,
                final_price: slot.final_price ?? null,
                member_discount: slot.member_discount ?? null,
              });
            }

            const merged = slotMap.get(timeKey)!;

            if (slot.is_available) {
              merged.is_available = true;
            }
            // Peak status is a superset: once any template marks this time peak,
            // the merged slot is peak regardless of other templates.
            if (slot.is_peak) {
              merged.is_peak = true;
            }

            // Merge courts, tagging each with its template_id for later pricing
            // lookup in the reducer. A court ID is the stable dedup key.
            const existingCourtIds = new Set(merged.available_court_ids);
            for (const court of slot.available_courts ?? []) {
              if (!existingCourtIds.has(court.id)) {
                existingCourtIds.add(court.id);
                merged.available_court_ids.push(court.id);
                merged.available_courts.push({
                  ...court,
                  template_id: tmplId,
                });
              }
            }

            // Merge resources (trainers / ball machines), tagging each with
            // its source template_id. Multiple trainer templates can share the
            // same duration (e.g. a coach-specific template alongside a general
            // one); the tag lets the reducer set `selectedTemplateId` correctly
            // when the user picks a trainer so package-credit lookup matches
            // the package the user actually owns.
            const existingResourceIds = new Set(merged.resource_ids ?? []);
            for (const resource of slot.available_resources ?? []) {
              if (!existingResourceIds.has(resource.id)) {
                existingResourceIds.add(resource.id);
                merged.resource_ids = merged.resource_ids ?? [];
                merged.resource_ids.push(resource.id);
                merged.resource_names = merged.resource_names ?? [];
                merged.resource_names.push(resource.name);
                merged.available_resources = merged.available_resources ?? [];
                merged.available_resources.push({
                  ...resource,
                  template_id: tmplId,
                });
              } else {
                // Same coach reappearing under a second same-duration template.
                // First-wins here used to be decided by unordered RPC row order,
                // nondeterministically hiding a coach-specific off-peak rate
                // behind the general one (Padel District Asaad & Hadi). When the
                // newcomer is a discount variant of the entry we kept (identical
                // peak price, cheaper off-peak — see trainer-price-collision.ts
                // for why plain cheapest-wins is NOT safe), replace the whole
                // entry so price AND template stamp stay coherent. Package
                // redemption tolerates either stamp: the server widens credit
                // matching to all of the coach's same-duration templates.
                const resources = merged.available_resources ?? [];
                const existingIdx = resources.findIndex(
                  (r) => r.id === resource.id,
                );
                const existing = resources[existingIdx];
                if (existing && isDiscountVariantOf(resource, existing)) {
                  resources[existingIdx] = {
                    ...resource,
                    template_id: tmplId,
                  };
                }
              }
            }
          }
        }

        const slots = Array.from(slotMap.values()).sort((a, b) => {
          const getTimeSortValue = (time: string) => {
            const [hours, minutes] = time.split(':').map(Number);
            const totalMinutes = (hours || 0) * 60 + (minutes || 0);
            return totalMinutes < 300 ? totalMinutes + 1440 : totalMinutes;
          };

          return (
            getTimeSortValue(a.start_time) - getTimeSortValue(b.start_time)
          );
        });

        // The templateId in SET_TIME_SLOTS is a fallback used by the package-credits
        // effect. Per-court pricing resolves via template_id on each SlotCourtInfo.
        dispatch({
          type: 'SET_TIME_SLOTS',
          payload: {
            timeSlots: slots,
            templateId: matchingTemplates[0]!.id,
          },
        });
      } catch (error) {
        if (abortController.signal.aborted) return;
        console.error('Error fetching time slots:', error);
        dispatch({
          type: 'SET_TIME_SLOTS',
          payload: { timeSlots: [], templateId: matchingTemplates[0]!.id },
        });
      }
    };

    fetchTimeSlots();

    return () => {
      abortController.abort();
    };
  }, [
    state.date,
    state.selectedDuration,
    state.selectedTemplateId,
    state.sport,
    state.bookingType,
    state.allTemplatesBySport,
    state.allDurationsByBookingType,
    state.allDurationsBySport,
    state.dynamicSlotSports,
    state.selectedLocationId,
    state.timeSlotsRefreshCount,
    businessType,
    userId,
    isUserLoading,
    // Re-fetch when the UI language changes so the Arabic court name/type
    // overlay is applied (or dropped) without needing another trigger.
    i18n.language,
    getDurationFromLabel,
    getAllBookingSlotsBatch,
    dispatch,
  ]);

  // -------------------------------------------------------------------
  // Effect 2: Fetch all classes for the month (class bookings only)
  // Fetches once per sport/bookingType/location change, not per date.
  // Date filtering happens in the reducer (SELECT_DATE, SET_ALL_CLASSES_FOR_MONTH).
  // -------------------------------------------------------------------
  const sportId = state.sport ? state.sportsMetadata[state.sport]?.id : undefined;
  const classAttendeeId = state.selectedBookingAccount?.id ?? userId;

  useEffect(() => {
    const isAll = state.bookingType === 'all';
    // 'all' and cross-sport category links (state.allSports) fetch across
    // every sport; any other class type needs a sport context.
    const acrossSports = isAll || state.allSports;
    if (!isClassType(state.bookingType) || (!state.sport && !acrossSports)) {
      return;
    }

    // A guest-mode fetch here would be discarded as soon as the session
    // resolves to a signed-in customer, costing a second 60-day class query.
    if (isUserLoading) {
      dispatch({ type: 'SET_CLASSES_LOADING', payload: true });
      return;
    }

    const abortController = new AbortController();

    const fetchAllClasses = async () => {
      dispatch({ type: 'SET_CLASSES_LOADING', payload: true });

      const startDate = formatDateLocal(new Date());
      // 60-day class-availability window. The previous 30 days greyed out real,
      // bookable sessions in the second half of the calendar — e.g. on Jun 25
      // the last selectable Tuesday was Jul 21 even though Jul 28 existed with
      // open spots. (The calendar itself will display up to a year ahead via
      // booking-form.tsx `dateRangeDays = isClassBooking ? 365`, so anything
      // beyond this window still falls back to greyed-out.)
      const endDate = new Date();
      endDate.setDate(endDate.getDate() + CLASS_AVAILABILITY_WINDOW_DAYS);
      const endDateStr = formatDateLocal(endDate);

      // Synthetic 'all' bookingType (unified class filter for studios) means
      // "no category filter, no sport filter" — pass null/undefined so the
      // RPC returns every class type for every sport.
      const categoryFilter = isAll ? null : state.bookingType;
      const sportIdFilter = acrossSports ? undefined : sportId;

      try {
        const result = await getAvailableClassesForDate(
          tenantSlug,
          startDate,
          sportIdFilter,
          categoryFilter,
          endDateStr,
          classAttendeeId,
          state.selectedLocationId,
        );

        if (abortController.signal.aborted) return;

        if (result.success && result.data) {
          dispatch({
            type: 'SET_ALL_CLASSES_FOR_MONTH',
            payload: result.data.filter((session) => !session.season),
          });
        } else {
          dispatch({ type: 'SET_ALL_CLASSES_FOR_MONTH', payload: [] });
        }
      } catch (error) {
        if (abortController.signal.aborted) return;
        console.error('Error fetching classes:', error);
        dispatch({ type: 'SET_ALL_CLASSES_FOR_MONTH', payload: [] });
      }
    };

    fetchAllClasses();

    return () => {
      abortController.abort();
    };
  }, [
    state.sport,
    state.bookingType,
    state.allSports,
    state.selectedLocationId,
    sportId,
    tenantSlug,
    classAttendeeId,
    isUserLoading,
    getAvailableClassesForDate,
    dispatch,
  ]);

  // -------------------------------------------------------------------
  // Effect 3: Check package credits
  // -------------------------------------------------------------------
  useEffect(() => {
    // Reset credits immediately when there is no selected template (i.e. the
    // user hasn't chosen a duration/time yet) or when not authenticated.
    // This ensures the payment method selector never shows stale credit counts.
    if (!userId || !state.selectedTemplateId) {
      dispatch({
        type: 'SET_PACKAGE_CREDITS',
        payload: {
          availableCredits: 0,
          hasMatchingDurationPackage: false,
          hasDateExcludedCredits: false,
        },
      });
      return;
    }

    // Using a boolean flag instead of AbortController because server actions
    // (unlike fetch) cannot be aborted mid-flight. The flag prevents stale
    // results from a previous invocation from overwriting newer state.
    let cancelled = false;

    const checkCredits = async () => {
      const packageType = mapBookingTypeToPackageType(state.bookingType);

      try {
        const result = await checkUserPackageCreditsAction({
          packageType,
          templateId: state.selectedTemplateId,
          targetUserId: state.selectedBookingAccount?.id,
          // Lets the check honour sport-restricted packages: the action resolves
          // this court's sport server-side. Empty until a court is picked.
          courtId: state.selectedCourt || undefined,
          // For trainer/ball-machine bookings, the server widens the template
          // match to the resource's discount-variant sibling templates (same
          // duration + identical peak price; general + resource-specific rate)
          // so a package bound to either shows the credit option. Ball machines
          // live in the same registration table (trainers rows with
          // resource_type='ball_machine').
          trainerId:
            state.selectedTrainer || state.selectedBallMachine || undefined,
          // Selected session date (tenant-local) so attend-by packages that
          // can't cover this date are excluded and the credit option auto-hides.
          bookingDate: state.date ? formatDateLocal(state.date) : undefined,
          numberOfParticipants: isClassType(state.bookingType)
            ? 1
            : state.numberOfParticipants,
        });

        if (cancelled) return;

        if (result.success && result.data) {
          dispatch({
            type: 'SET_PACKAGE_CREDITS',
            payload: {
              availableCredits: result.data.availableCredits,
              hasMatchingDurationPackage: result.data.hasMatchingTemplate,
              hasDateExcludedCredits: result.data.hasDateExcludedCredits ?? false,
              numberOfParticipants: state.numberOfParticipants,
            },
          });
        }
      } catch (error) {
        if (cancelled) return;
        console.error('Error checking package credits:', error);
        // On error, leave the current credit count unchanged rather than
        // resetting to 0, which would silently prevent package payments.
      }
    };

    checkCredits();

    return () => {
      cancelled = true;
    };
  }, [
    userId,
    state.bookingType,
    state.selectedTemplateId,
    state.selectedBookingAccount?.id,
    // Re-check when the court changes: a different court may be a different
    // sport, which changes whether a sport-restricted package is redeemable.
    state.selectedCourt,
    // Re-check when the coach / ball machine changes: template matching widens
    // to the resource's sibling same-duration templates, so a different
    // resource can change which packages apply.
    state.selectedTrainer,
    state.selectedBallMachine,
    state.numberOfParticipants,
    // Re-check when the selected date changes: attend-by credits are only
    // offered for a date on/before their package expiry.
    state.date,
    checkUserPackageCreditsAction,
    dispatch,
  ]);

  // -------------------------------------------------------------------
  // Effect 4: Auto-select first available duration
  // Skipped when the booking type shows a service list (same-duration templates
  // that share a court, or any trainer/ball-machine collision): there the
  // customer picks a named service that sets duration + template together via
  // SELECT_SERVICE, so we must not pre-select a bare duration on their behalf.
  // -------------------------------------------------------------------
  useEffect(() => {
    if (!state.date || !state.sport || isClassType(state.bookingType)) return;
    if (state.selectedDuration) return;
    const typeTemplates = (
      state.allTemplatesBySport[state.sport] ?? []
    ).filter((tmpl) => {
      if (state.bookingType === 'court') return tmpl.has_court_booking;
      if (state.bookingType === 'trainer') return tmpl.has_trainer_booking;
      if (state.bookingType === 'ball_machine')
        return tmpl.has_ball_machine_booking;
      return false;
    });
    if (shouldShowServiceList(state.bookingType, typeTemplates, businessType))
      return;

    const firstAvailable = durationOptions.find((opt) => opt.isAvailable);
    if (firstAvailable) {
      dispatch({ type: 'SELECT_DURATION', payload: firstAvailable.label });
    }
  }, [
    state.date,
    state.sport,
    state.bookingType,
    state.selectedDuration,
    state.allTemplatesBySport,
    durationOptions,
    businessType,
    dispatch,
  ]);

  // -------------------------------------------------------------------
  // Apple Pay availability check
  // -------------------------------------------------------------------
  // Native Apple Pay is a Moyasar-only integration. For every other provider
  // (Stripe, Tap, HyperPay) Apple Pay lives inside the provider's own checkout
  // sheet, so we must never treat it as available here — otherwise the default
  // 'applepay' payment method would render an unselectable radio and the wrong
  // "Book with Apple Pay" button on those tenants.
  useEffect(() => {
    const deviceSupportsApplePay =
      typeof window !== 'undefined' &&
      Boolean((window as unknown as Record<string, unknown>).ApplePaySession);

    dispatch({
      type: 'SET_APPLE_PAY_AVAILABLE',
      payload: deviceSupportsApplePay && provider === 'moyasar',
    });
  }, [dispatch, provider]);

  return {
    durationOptions,
    getDurationFromLabel,
    formatDuration,
  };
}
